import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { Card, CardBody, CardHeader, CardTitle } from '@progress/kendo-react-layout';
import { paths } from '../../../routes';
import { fetchApi } from '../../../services/api';
import { Title } from '../../../utils/title';
import { EditableField } from '../../../components/EditableField';
import useConfirm from '../../../components/useConfirm';
import PartTagsInput, { parsePartTags } from '../PartTagsInput';
import RestockPartDialog from '../dialogs/RestockPartDialog';
import AdjustInventoryDialog from '../dialogs/AdjustInventoryDialog';
import { JsonResponse } from '../../../TypeGen/json-response';

interface PartDetailViewModel {
  PartID: number;
  PartNumber: string;
  Description?: string;
  Tags?: string;
  QuantityInStock: number;
  AverageCost: number;
  MinQuantity: number;
  MaxQuantity: number;
  UnitOfMeasure: string;
  LastRestockDate?: string;
  LastUsedDate?: string;
  Note?: string;
  IsActive: boolean;
  CreatedDateTime: string;
}

interface PartLocationViewModel {
  PartLocationID: number;
  LocationID: number;
  LocationName: string;
  QuantityOnHand: number;
  MaxQuantity?: number;
  LastRestockDate?: string;
  LastUsedDate?: string;
  Note?: string;
}

interface RecentTransactionViewModel {
  PartTransactionID: number;
  LocationID?: number | null;
  QuantityChange: number;
  CostPerUnit: number;
  TransactionDateTime: string;
  TransactionType: string;
  Note?: string;
  CreatedBy: string;
  CreatedByName: string;
  WorkOrderID?: number | null;
  PurchaseOrderNumber?: number | null;
}

interface InventoryDetailResponse {
  Part: PartDetailViewModel;
  Locations: PartLocationViewModel[];
  RecentTransactions: RecentTransactionViewModel[];
}

interface RouteParams {
  partId: string;
}

const ShopInventoryDetail: React.FC = () => {
  const { partId } = useParams<RouteParams>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<InventoryDetailResponse | null>(null);
  const [showRestockDialog, setShowRestockDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const { ConfirmationDialog, confirm } = useConfirm({
    labels: { confirm: 'Retire Part', cancel: 'Cancel' },
    themeColor: 'warning'
  });

  const loadData = useCallback(async () => {
    if (!partId) return;

    setLoading(true);
    try {
      const response: InventoryDetailResponse = await fetchApi(`/api/ShopInventory/PartDetail?PartID=${partId}`);
      setData(response);
    } catch (error) {
      console.error('Error loading part details:', error);
    } finally {
      setLoading(false);
    }
  }, [partId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updatePart = useCallback(async (changes: Partial<PartDetailViewModel>) => {
    if (!data) {
      return;
    }

    const updatedPart: PartDetailViewModel = {
      ...data.Part,
      ...changes
    };

    const payload = {
      PartID: updatedPart.PartID,
      PartNumber: updatedPart.PartNumber,
      Description: updatedPart.Description ?? null,
      Tags: updatedPart.Tags ?? null,
      MinQuantity: updatedPart.MinQuantity,
      MaxQuantity: updatedPart.MaxQuantity,
      UnitOfMeasure: updatedPart.UnitOfMeasure,
      Note: updatedPart.Note ?? null,
      IsActive: updatedPart.IsActive
    };

    try {
      const response: JsonResponse = await fetchApi('/api/ShopInventory/UpdatePart', payload, 'POST');

      if (response.Success === false) {
        throw new Error(response.ErrorMessage || 'Failed to update part.');
      }

      setData(prev => prev ? ({ ...prev, Part: updatedPart }) : prev);
    } catch (error: any) {
      if (error?.status) {
        throw error;
      }

      if (error instanceof Error && error.message) {
        alert(error.message);
        throw error;
      }

      alert('Failed to update part.');
      throw error;
    }
  }, [data]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getTransactionBadgeColor = (type: string) => {
    switch (type) {
      case 'Restock': return 'success';
      case 'Usage': return 'warning';
      case 'Return': return 'info';
      case 'Adjustment': return 'secondary';
      default: return 'primary';
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center p-4">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger">
          Part not found or error loading data.
        </div>
      </div>
    );
  }

  const { Part, Locations, RecentTransactions } = data;
  const isNoStock = Part.QuantityInStock === 0;
  const isLowStock = Part.QuantityInStock <= Part.MinQuantity && Part.QuantityInStock > 0;

  return <>
    <Title string={`${Part.PartNumber} - Shop Inventory`} />
    <nav className="navbar navbar-expand-lg navbar-dark bg-dark mt-3">
      <span className="navbar-brand">
        <Link to={paths.ShopInventory}>Shop Inventory</Link>
        <span className="text-muted">
          <i className="fas fa-chevron-right mx-2"></i>
          {Part.PartNumber}
        </span>
        {!Part.IsActive && <span className="badge badge-secondary ml-2">Retired</span>}
      </span>
      <button className="navbar-toggler" type="button" data-toggle="collapse" data-target="#inventoryNav" aria-controls="inventoryNav" aria-expanded="false" aria-label="Toggle navigation">
        <span className="navbar-toggler-icon"></span>
      </button>
      <div className="collapse navbar-collapse" id="inventoryNav">
        <ul className="navbar-nav ml-auto">
          <li className="nav-item mr-2">
            <Button
              themeColor={Part.IsActive ? 'warning' : 'success'}
              onClick={async () => {
                if (Part.IsActive && !(await confirm(`Retire ${Part.PartNumber}?`))) return;
                updatePart({ IsActive: !Part.IsActive });
              }}
            >
              {Part.IsActive ? 'Retire Part' : 'Reactivate Part'}
            </Button>
          </li>
          <li className="nav-item mr-2">
            <Button
              themeColor="secondary"
              onClick={() => setShowAdjustDialog(true)}
              disabled={Locations.length === 0}
            >
              Adjust Inventory
            </Button>
          </li>
          <li className="nav-item">
            <Button
              themeColor="success"
              onClick={() => setShowRestockDialog(true)}
            >
              Restock Part
            </Button>
          </li>
        </ul>
      </div>
    </nav>

    {/* Part Details */}
    <div className="row mb-4 mt-3">
      <div className="col-md-6">
        <Card>
          <CardHeader>
            <CardTitle>Part Information</CardTitle>
          </CardHeader>
          <CardBody>
            <div className="row m-2">
              <div className="col-md-6">
                <div className="form-group row">
                  <label className="col-sm-4 col-form-label"><strong>Part Number:</strong></label>
                  <div className="col-sm-8">
                    <EditableField
                      required
                      dataType="string"
                      inputProps={{ maxLength: 64 }}
                      data={Part.PartNumber}
                      save={(value) => updatePart({ PartNumber: value })}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group row">
                  <label className="col-sm-4 col-form-label"><strong>Unit of Measure:</strong></label>
                  <div className="col-sm-8">
                    <EditableField
                      required
                      dataType="string"
                      inputProps={{ maxLength: 16 }}
                      data={Part.UnitOfMeasure}
                      save={(value) => updatePart({ UnitOfMeasure: value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="row m-2">
              <div className="col-md-12">
                <div className="form-group row">
                  <label className="col-sm-2 col-form-label"><strong>Description:</strong></label>
                  <div className="col-sm-10">
                    <EditableField
                      dataType="string"
                      data={Part.Description ?? ''}
                      inputProps={{ maxLength: 256, placeholder: 'Add description' }}
                      renderDisplay={(value) => value ? value : <span className="text-muted">Add description</span>}
                      save={(value) => updatePart({ Description: value?.trim() ? value : undefined })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="row m-2">
              <div className="col-md-12">
                <div className="form-group row">
                  <label className="col-sm-2 col-form-label"><strong>Tags:</strong></label>
                  <div className="col-sm-10">
                    <EditableField
                      dataType="string"
                      data={Part.Tags ?? ''}
                      renderDisplay={(value) => {
                        const tags = parsePartTags(value);
                        return tags.length === 0
                          ? <span className="text-muted">Add tags</span>
                          : <>{tags.map((tag) => <span key={tag} className="badge badge-secondary mr-1">{tag}</span>)}</>;
                      }}
                      renderEditable={(value, saving, onChange) => (
                        <PartTagsInput
                          className="k-dropdown-wrap-append flex-fill"
                          value={parsePartTags(value)}
                          disabled={saving}
                          onChange={(tags) => onChange(tags.join(','))}
                        />
                      )}
                      save={(value) => updatePart({ Tags: value?.trim() ? value : undefined })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="row m-2">
              <div className="col-md-6">
                <div className="form-group row">
                  <label className="col-sm-4 col-form-label"><strong>Quantity in Stock:</strong></label>
                  <div className="col-sm-8 d-flex align-items-center">
                    <span className="mr-2">{Part.QuantityInStock} {Part.UnitOfMeasure}</span>
                    {isLowStock && <span className="badge badge-warning ml-2">Low Stock</span>}
                    {isNoStock && <span className="badge badge-danger ml-2">No Stock</span>}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group row">
                  <label className="col-sm-4 col-form-label"><strong>Average Cost:</strong></label>
                  <div className="col-sm-8 d-flex align-items-center">
                    {formatCurrency(Part.AverageCost)}
                  </div>
                </div>
              </div>
            </div>

            <div className="row m-2">
              <div className="col-md-6">
                <div className="form-group row">
                  <label className="col-sm-4 col-form-label"><strong>Min Quantity:</strong></label>
                  <div className="col-sm-8">
                    <EditableField
                      required
                      dataType="number"
                      data={Part.MinQuantity}
                      inputProps={{ min: 0, format: 'n0', step: 1 }}
                      renderDisplay={(value) => `${value ?? 0} ${Part.UnitOfMeasure}`}
                      save={(value) => updatePart({ MinQuantity: value ?? 0 })}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group row">
                  <label className="col-sm-4 col-form-label"><strong>Max Quantity:</strong></label>
                  <div className="col-sm-8">
                    <EditableField
                      dataType="number"
                      data={Part.MaxQuantity}
                      inputProps={{ min: 0, format: 'n0', step: 1 }}
                      renderDisplay={(value) => (value && value > 0) ? `${value} ${Part.UnitOfMeasure}` : 'No Limit'}
                      save={(value) => updatePart({ MaxQuantity: value ?? 0 })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="row m-2">
              <div className="col-md-6">
                <div className="form-group row">
                  <label className="col-sm-4 col-form-label"><strong>Last Used:</strong></label>
                  <div className="col-sm-8 d-flex align-items-center">
                    {formatDate(Part.LastUsedDate)}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group row">
                  <label className="col-sm-4 col-form-label"><strong>Last Restocked:</strong></label>
                  <div className="col-sm-8 d-flex align-items-center">
                    {formatDate(Part.LastRestockDate)}
                  </div>
                </div>
              </div>
            </div>

            <div className="row m-2">
              <div className="col-md-12">
                <div className="form-group row">
                  <label className="col-sm-2 col-form-label"><strong>Note:</strong></label>
                  <div className="col-sm-10">
                    <EditableField
                      dataType="string"
                      data={Part.Note ?? ''}
                      renderDisplay={(value) => value ? value : <span className="text-muted">Add note</span>}
                      renderEditable={(value, saving, onChange) => (
                        <textarea
                          className="form-control"
                          rows={3}
                          value={value}
                          disabled={saving}
                          maxLength={500}
                          onChange={(e) => onChange(e.target.value)}
                        />
                      )}
                      save={(value) => updatePart({ Note: value?.trim() ? value : undefined })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="col-md-6">
        <Card>
          <CardHeader>
            <CardTitle>Storage Locations</CardTitle>
          </CardHeader>
          <CardBody>
            {(() => {
              const activeLocations = Locations.filter(l => l.QuantityOnHand > 0);
              return activeLocations.length === 0 ? (
                <p className="text-muted">No storage locations configured.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Location</th>
                        <th>Quantity</th>
                        <th>Max Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeLocations.map((location) => (
                        <tr key={location.PartLocationID}>
                          <td>{location.LocationName}</td>
                          <td>{location.QuantityOnHand} {Part.UnitOfMeasure}</td>
                          <td>{location.MaxQuantity || 'No Limit'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </CardBody>
        </Card>
      </div>
    </div>

    {/* Recent Transactions */}
    <div className="row">
      <div className="col-12">
        <Card>
          <CardHeader>
            <CardTitle>Recent Transactions</CardTitle>
          </CardHeader>
          <CardBody>
            {RecentTransactions.length === 0 ? (
              <p className="text-muted">No recent transactions.</p>
            ) : (
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Work Order</th>
                      <th>Purchase Order</th>
                      <th>Quantity Change</th>
                      <th>Cost Per Unit</th>
                      <th>Created By</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RecentTransactions.map((transaction) => (
                      <tr key={transaction.PartTransactionID}>
                        <td>{formatDateTime(transaction.TransactionDateTime)}</td>
                        <td>
                          <span className={`badge badge-${getTransactionBadgeColor(transaction.TransactionType)}`}>
                            {transaction.TransactionType}
                          </span>
                        </td>
                        <td>
                          {transaction.WorkOrderID ? (
                            <Link to={`/Shop/WorkOrders/${transaction.WorkOrderID}`}>#{transaction.WorkOrderID}</Link>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </td>
                        <td>
                          {transaction.PurchaseOrderNumber ? (
                            <Link to={`/PurchaseOrder/Index/${transaction.PurchaseOrderNumber}`}>#{transaction.PurchaseOrderNumber}</Link>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </td>
                        <td>
                          <span className={transaction.QuantityChange > 0 ? 'text-success' : 'text-danger'}>
                            {transaction.QuantityChange > 0 ? '+' : ''}{transaction.QuantityChange} {Part.UnitOfMeasure}
                          </span>
                        </td>
                        <td>{formatCurrency(transaction.CostPerUnit)}</td>
                        <td>
                          {transaction.CreatedByName?.trim()
                            || (transaction.CreatedBy === '00000000-0000-0000-0000-000000000000' ? 'System' : transaction.CreatedBy)
                            || 'N/A'}
                        </td>
                        <td>{transaction.Note || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
    </div>
  </div>

    {showAdjustDialog && (
      <AdjustInventoryDialog
        partId={Part.PartID}
        partNumber={Part.PartNumber}
        unitOfMeasure={Part.UnitOfMeasure}
        locations={Locations.map(({ LocationID, LocationName, QuantityOnHand }) => ({ LocationID, LocationName, QuantityOnHand }))}
        onClose={() => setShowAdjustDialog(false)}
        onSuccess={() => loadData()}
      />
    )}

    {showRestockDialog && (
      <RestockPartDialog
        partId={Part.PartID}
        partNumber={Part.PartNumber}
        unitOfMeasure={Part.UnitOfMeasure}
        defaultLocationId={RecentTransactions.find(t => t.LocationID)?.LocationID
          ?? Locations.find(l => l.QuantityOnHand > 0)?.LocationID
          ?? null}
        onClose={() => setShowRestockDialog(false)}
        onSuccess={() => loadData()}
      />
    )}

    <ConfirmationDialog />
  </>;
};

export default ShopInventoryDetail;
